// @flow
import * as React from 'react';
import invariant from 'invariant';
import type { Location } from 'react-router';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import getSearchUrl from 'utils/search/getSearchUrl';
import {
  fetchPage as fetchPageAction,
  setPageIndex as setPageIndexAction
} from '../redux/main/actions/mainSearch';

export type SearchContext = {
  searchResultsPath: Object,
  previousResult: ?Object,
  previousResultPageIndex: ?number,
  nextResult: ?Object,
  nextResultPageIndex: ?number,
  resultIndex: number,
  totalResults: number,
  setPageIndex: typeof setPageIndexAction
};

type Props = {
  render: (?SearchContext) => React.Node,
  searchContext: {
    currentResultIndex: number,
    pageIndex: number,
    resultsByPage: { [number]: Object },
    searchResultsPath: Object,
    previousResult: ?Object,
    previousResultPageIndex: ?number,
    nextResult: ?Object,
    nextResultPageIndex: ?number,
    originatingPathname: ?string
  },
  isSearching: boolean,
  fetchPage: typeof fetchPageAction,
  setSearchPageIndex: typeof setPageIndexAction,
  location: Location
};

class SearchContextHeaderContainer extends React.PureComponent<Props> {
  componentDidMount() {
    if (!this.props.searchContext || this.props.isSearching) {
      return;
    }
    const {
      currentResultIndex,
      pageIndex,
      resultsByPage
    } = this.props.searchContext;
    const results = resultsByPage[pageIndex];

    // pre-fetch the previous page of results if we're on one of the first two results in the page
    if (
      currentResultIndex <= 1 &&
      pageIndex > 0 &&
      !resultsByPage[pageIndex - 1]
    ) {
      this.props.fetchPage(pageIndex - 1);
    }

    // pre-fetch the next page of results if we're on one of the last two results in the page
    if (
      currentResultIndex >= results.hitsPerPage - 2 &&
      pageIndex < results.nbPages - 1 &&
      !resultsByPage[pageIndex + 1]
    ) {
      this.props.fetchPage(pageIndex + 1);
    }
  }

  render() {
    if (!this.props.searchContext) {
      return this.props.render();
    }
    const {
      currentResultIndex,
      pageIndex,
      resultsByPage,
      searchResultsPath,
      previousResult,
      previousResultPageIndex,
      nextResult,
      nextResultPageIndex,
      originatingPathname
    } = this.props.searchContext;

    const results = resultsByPage[pageIndex];
    const resultIndex = results.hitsPerPage * results.page + currentResultIndex;

    const searchContext = {
      searchResultsPath,
      previousResult,
      previousResultPageIndex,
      nextResult,
      nextResultPageIndex,
      resultIndex,
      originatingPathname,
      totalResults: results.nbHits,
      setPageIndex: this.props.setSearchPageIndex
    };

    return this.props.render(searchContext);
  }
}

const mapStateToProps = (
  { mainSearch, currentUser, searchRadius },
  { currentObjectId, location }
) => {
  const {
    filters,
    searchFacets,
    pageIndex,
    query,
    resultsByPage,
    sort,
    isSearching
  } = mainSearch;

  if (!location.state || !location.state.showSearchContext) {
    return {};
  }

  const results = resultsByPage[pageIndex];
  if (!results) {
    return {};
  }

  const currentResultIndex = results.hits.findIndex(
    hit => hit.objectID === currentObjectId
  );

  if (currentResultIndex === -1) {
    return {};
  }

  let previousResult = null;
  let previousResultPageIndex = null;

  if (currentResultIndex > 0) {
    previousResult = resultsByPage[pageIndex].hits[currentResultIndex - 1];
    previousResultPageIndex = pageIndex;
  } else if (pageIndex > 0) {
    const previousPage = resultsByPage[pageIndex - 1];
    if (previousPage) {
      previousResult = previousPage.hits[previousPage.hitsPerPage - 1];
      if (previousResult) {
        previousResultPageIndex = pageIndex - 1;
      }
    }
  }

  let nextResult = null;
  let nextResultPageIndex = null;

  if (currentResultIndex < results.hitsPerPage - 1) {
    nextResult = results.hits[currentResultIndex + 1];
    nextResultPageIndex = results.page;
  } else if (pageIndex < results.nbPages - 1) {
    const nextPage = resultsByPage[pageIndex + 1];
    if (nextPage) {
      nextResult = nextPage.hits[0];
      nextResultPageIndex = pageIndex + 1;
    }
  }
  const originatingPathname =
    location.state && location.state.originatingPathname;
  invariant(filters.type, 'Filter type required for main search url');
  const searchResultsPath = getSearchUrl({
    filters,
    searchFacets,
    query,
    pageIndex,
    sort,
    originatingPathname,
    location: currentUser.searchLocation,
    radius: searchRadius.value,
    scrollToId: currentObjectId,
    currentSearchParams: location.search
  });
  return {
    searchContext: {
      currentResultIndex,
      pageIndex,
      resultsByPage,
      searchResultsPath,
      previousResult,
      previousResultPageIndex,
      nextResult,
      nextResultPageIndex,
      originatingPathname
    },
    isSearching,
    location
  };
};

export default withRouter(
  connect(
    mapStateToProps,
    {
      fetchPage: fetchPageAction,
      setSearchPageIndex: setPageIndexAction
    }
  )(SearchContextHeaderContainer)
);
